import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SchedulingScenarioService } from '@principle-theorem/ng-appointment';
import {
  CurrentScopeFacade,
  OrganisationService,
  ScheduleSummaryEventActionsService,
  SummaryEventActionType,
} from '@principle-theorem/ng-principle-shared';
import {
  confirmationDialogData,
  ConfirmDialogComponent,
  DialogPresets,
  type IConfirmationDialogInput,
} from '@principle-theorem/ng-shared';
import {
  Brand,
  CalendarEvent,
  Schedule,
} from '@principle-theorem/principle-core';
import {
  type ICalendarEvent,
  type IBrand,
  type CalendarEventFromSchedule,
  type ICalendarEventSchedule,
  isCalendarEvent,
  isCalendarEventFromSchedule,
  ScheduleModifierType,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  type IReffable,
  Firestore,
  isWithRef,
  snapshot,
  type Timestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { FirebaseError } from 'firebase/app';
import { AddCalendarEventComponent } from './components/add-calendar-event/add-calendar-event.component';
import {
  EditCalendarEventComponent,
  IEditCalendarEventDialogData,
} from './components/edit-calendar-event/edit-calendar-event.component';

@Injectable()
export class ManageCalendarEventService {
  constructor(
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private _currentScope: CurrentScopeFacade,
    private _schedulingScenario: SchedulingScenarioService,
    private _scheduleSummaryEventActions: ScheduleSummaryEventActionsService,
    private _organisation: OrganisationService
  ) {}

  async add(
    calendarEvent?: ICalendarEvent
  ): Promise<DocumentReference<ICalendarEvent> | undefined> {
    const config: MatDialogConfig = DialogPresets.medium({
      autoFocus: true,
      data: { calendarEvent },
    });
    const response = await this._dialog
      .open<AddCalendarEventComponent, undefined, ICalendarEvent>(
        AddCalendarEventComponent,
        config
      )
      .afterClosed()
      .toPromise();

    if (!isCalendarEvent(response)) {
      return;
    }
    return this._save(response);
  }

  async openEditDialog(
    calendarEvent: WithRef<ICalendarEvent>
  ): Promise<DocumentReference<ICalendarEvent> | undefined> {
    const config: MatDialogConfig = DialogPresets.medium({
      autoFocus: true,
      data: { calendarEvent },
    });
    const result = await this._dialog
      .open<
        EditCalendarEventComponent,
        IEditCalendarEventDialogData,
        WithRef<ICalendarEvent>
      >(EditCalendarEventComponent, config)
      .afterClosed()
      .toPromise();

    if (!result) {
      return;
    }

    return this.update(result);
  }

  async update(
    calendarEvent: WithRef<ICalendarEvent> | ICalendarEvent
  ): Promise<DocumentReference<ICalendarEvent> | undefined> {
    if (!(await this._canManage(calendarEvent))) {
      return;
    }
    if (isWithRef(calendarEvent)) {
      const isBlockedByDoubleBooking =
        await this._schedulingScenario.isBlockedByDoubleBooking(calendarEvent);
      if (isBlockedByDoubleBooking) {
        return;
      }

      try {
        await Firestore.patchDoc(calendarEvent.ref, { ...calendarEvent });
      } catch (error: unknown) {
        return this._handleWriteError(error);
      }
      await this._scheduleSummaryEventActions.setCalendarEvent(
        SummaryEventActionType.Update,
        calendarEvent
      );
      return calendarEvent.ref;
    }

    if (isCalendarEventFromSchedule(calendarEvent)) {
      return this.replaceScheduledOccurrence(calendarEvent);
    }

    return this._save(calendarEvent);
  }

  async confirmDelete(prompt: string): Promise<boolean | undefined> {
    const data = confirmationDialogData({
      title: 'Delete Calendar Event',
      prompt,
      submitLabel: 'Delete',
      submitColor: 'warn',
    });
    return this._dialog
      .open<ConfirmDialogComponent, IConfirmationDialogInput, boolean>(
        ConfirmDialogComponent,
        DialogPresets.small({ data })
      )
      .afterClosed()
      .toPromise();
  }

  async removeOccurenceFromSchedule(
    scheduleRef: DocumentReference<ICalendarEventSchedule>,
    date: Timestamp
  ): Promise<void> {
    const schedule = await Firestore.getDoc(scheduleRef);
    await Firestore.patchDoc(
      schedule.ref,
      Schedule.addModifier(schedule, {
        type: ScheduleModifierType.Delete,
        dates: [date],
      })
    );
  }

  async replaceScheduledOccurrence(
    calendarEvent: ICalendarEvent
  ): Promise<DocumentReference<ICalendarEvent> | undefined> {
    if (
      !(await this._canManage(calendarEvent)) ||
      !isCalendarEventFromSchedule(calendarEvent)
    ) {
      return;
    }
    const isBlocked = await this._schedulingScenario.isBlockedByDoubleBooking(
      calendarEvent,
      undefined,
      { scheduleRef: calendarEvent.scheduleRef, date: calendarEvent.event.from }
    );
    if (isBlocked) {
      return;
    }
    const ref = await this._writeScheduledOccurrence(calendarEvent);
    if (!ref) {
      return;
    }
    await this._scheduleSummaryEventActions.setCalendarEvent(
      SummaryEventActionType.Update,
      { ...calendarEvent, ref }
    );
    this._snackBar.open('Event Added');
    return ref;
  }

  async deleteScheduledOccurrence(
    calendarEvent: ICalendarEvent
  ): Promise<DocumentReference<ICalendarEvent> | undefined> {
    if (
      !(await this._canManage(calendarEvent)) ||
      !isCalendarEventFromSchedule(calendarEvent)
    ) {
      return;
    }
    return this._writeScheduledOccurrence({ ...calendarEvent, deleted: true });
  }

  private async _writeScheduledOccurrence(
    calendarEvent: CalendarEventFromSchedule
  ): Promise<DocumentReference<ICalendarEvent> | undefined> {
    const brand = await snapshot(this._currentScope.currentBrand$);
    if (!brand) {
      return;
    }
    try {
      return await Firestore.runTransaction(async (transaction) => {
        const schedule = await Firestore.getDoc(
          calendarEvent.scheduleRef,
          transaction
        );
        const ref = await Firestore.addDoc(
          Brand.calendarEventCol(brand),
          calendarEvent,
          undefined,
          transaction
        );
        await Firestore.patchDoc(
          schedule.ref,
          Schedule.addModifier(schedule, {
            type: ScheduleModifierType.Delete,
            dates: [calendarEvent.event.from],
          }),
          transaction
        );
        return ref;
      });
    } catch (error: unknown) {
      return this._handleWriteError(error);
    }
  }

  private async _canManage(calendarEvent: ICalendarEvent): Promise<boolean> {
    const permissions = await snapshot(this._organisation.userPermissions$);
    const canManage = CalendarEvent.canManageType(
      calendarEvent.event.type,
      permissions
    );
    if (!canManage) {
      this._snackBar.open('You do not have permission to edit this event.');
    }
    return canManage;
  }

  private _handleWriteError(error: unknown): undefined {
    if (error instanceof FirebaseError && error.code === 'permission-denied') {
      this._snackBar.open('You do not have permission to edit this event.');
      return;
    }
    throw error;
  }

  private async _save(
    calendarEvent: ICalendarEvent
  ): Promise<DocumentReference<ICalendarEvent> | undefined> {
    if (!(await this._canManage(calendarEvent))) {
      return;
    }
    const isBlockedByDoubleBooking =
      await this._schedulingScenario.isBlockedByDoubleBooking(calendarEvent);
    if (isBlockedByDoubleBooking) {
      return;
    }

    const brand = await snapshot(this._currentScope.currentBrand$);
    if (!brand) {
      // eslint-disable-next-line no-console
      console.error(
        'ManageCalendarEventService _save - No Brand in Current Scope'
      );
      return;
    }
    const docRef = await this._addCalendarEvent(brand, calendarEvent);
    if (!docRef) {
      return;
    }

    const newCalendarEvent = { ...calendarEvent, ref: docRef };

    if (isCalendarEventFromSchedule(calendarEvent)) {
      await this._scheduleSummaryEventActions.setCalendarEvent(
        SummaryEventActionType.Update,
        newCalendarEvent
      );
    } else {
      await this._scheduleSummaryEventActions.setCalendarEvent(
        SummaryEventActionType.Create,
        newCalendarEvent
      );
    }

    this._snackBar.open('Event Added');
    return docRef;
  }

  private async _addCalendarEvent(
    brand: IReffable<IBrand>,
    calendarEvent: ICalendarEvent
  ): Promise<DocumentReference<ICalendarEvent> | undefined> {
    try {
      return await Firestore.addDoc(
        Brand.calendarEventCol(brand),
        calendarEvent
      );
    } catch (error: unknown) {
      return this._handleWriteError(error);
    }
  }
}
